/* screens/workspace/canvas/InventoryCanvas.jsx — inline-канвас анализа инвентаризации (S6).
   Read-only: метрики, группы причин, ТОП недостач/излишков (Δ в display-единицах, тренд по смыслу),
   диагностика (статус из backend-словаря/эффект/критерии/рекомендация/нужные данные), план, сценарии.
   Поля совпадают с боевым адаптером `adaptInventory` и ReportDetail.InventoryReport (отчёт —
   генерируемый артефакт; правка result отложена). ХЕНДОФФ → frontend/components/chat/InventoryCanvas.jsx. */

import { Pill } from '../../../components/ui/Badge.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { InventoryAnalysisReport } from '../../../components/reports/InventoryAnalysisReport.jsx';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { CanvasErrorState, CanvasMetrics, CanvasSection, CanvasSkeleton, CanvasViewForm, TrendCell, useCanvasLoad } from './CanvasViewForm.jsx';

  const { useNavigate } = RR;

  // Статус диагноза инвентаризации (reports/schemas.py InventoryDiagnosis.status) — backend-значения.
  const INV_STATUS = {
    confirmed: { tone: 'bad', label: 'подтверждено' },
    awaiting_data: { tone: 'warn', label: 'ждём данные' },
    to_recount: { tone: 'neutral', label: 'пересчёт' },
  };

  function DeltaTable({ rows }) {
    return (
      <div className="cv-table-wrap">
        <table className="cv-table">
          <thead><tr><th>Ингредиент</th><th className="num">Δ, кол-во</th><th className="num">Δ, ₽</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td data-label="Ингредиент"><span className="cv-strong">{r.ingredient}</span></td>
                <td className="num" data-label="Δ, кол-во">{r.delta_units} {r.unit}</td>
                <td data-label="Δ, ₽"><TrendCell deltaRub={r.delta_rub} goodDirection="up" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function InventoryCanvas({ spec, onClose, panel }) {
    const { kind, data, error, retry } = useCanvasLoad(spec);
    const navigate = useNavigate();

    const id = data ? data.id : spec.id;
    const openHref = `/reports/${id}`;
    const header = { eyebrow: 'Анализ инвентаризации', icon: 'box', title: (data && data.title) || 'Анализ инвентаризации', panel };

    if (kind === 'loading') return <CanvasViewForm {...header} onClose={onClose}><CanvasSkeleton note="Сверяем ведомости…" /></CanvasViewForm>;
    if (kind === 'error') return <CanvasViewForm {...header} onClose={onClose}><CanvasErrorState error={error} message="Не удалось загрузить отчёт" notFoundMessage="Отчёт не найден или был удалён." onRetry={retry} /></CanvasViewForm>;
    if (kind === 'partial') return <CanvasViewForm {...header} onClose={onClose}><CanvasSkeleton note="Сверяем ведомости…" /></CanvasViewForm>;

    const r = data.result;

    // v2 (детерминированный пайплайн): единый рендерер с кликабельными
    // тегами критериев — общий с детальной страницей (/reports/:id).
    if (r && r.v2) {
      const v2footer = (
        <div className="cv-foot-actions">
          <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(openHref)}><span className="btn__label">Открыть отчёт</span><Icon name="arrow-up-right" size={14} /></button>
        </div>
      );
      return (
        <CanvasViewForm {...header} onClose={onClose} openHref={openHref} openLabel="Открыть" footer={v2footer}>
          <InventoryAnalysisReport r={r} compact />
        </CanvasViewForm>
      );
    }

    const mt = r.metrics || {};
    // Период/выручка опциональны (зависят от того, что распознали/прислали):
    // пустые плитки с «—» не показываем.
    const metrics = [
      ...(mt.months_count != null ? [{ label: 'Период, мес.', value: mt.months_count }] : []),
      ...(mt.revenue_total != null ? [{ label: 'Выручка', value: fmt.money(mt.revenue_total) }] : []),
      { label: 'Недостачи', value: fmt.money(mt.shortage_total_rub), tone: 'bad' },
      { label: 'Излишки', value: fmt.money(mt.surplus_total_rub), tone: 'info' },
    ];

    const body = (
      <>
        <CanvasMetrics items={metrics} />
        {r.groups && r.groups.length > 0 && (
          <CanvasSection title="Причины расхождений">
            <div className="cv-chips">
              {r.groups.map((g, i) => <span className="cv-causechip" key={i}>{g.cause} <span className="num">{g.count}</span></span>)}
            </div>
          </CanvasSection>
        )}
        <CanvasSection title={`ТОП недостач (${(r.top_shortages || []).length})`}><DeltaTable rows={r.top_shortages || []} /></CanvasSection>
        <CanvasSection title={`ТОП излишков (${(r.top_surpluses || []).length})`}><DeltaTable rows={r.top_surpluses || []} /></CanvasSection>
        <CanvasSection title="Диагностика">
          <div className="cv-table-wrap">
            <table className="cv-table">
              <thead><tr><th>Ингредиент</th><th>Статус</th><th className="num">Эффект</th><th>Рекомендация</th><th>Нужные данные</th></tr></thead>
              <tbody>
                {(r.diagnostics || []).map((dg, i) => {
                  const st = INV_STATUS[dg.status] || INV_STATUS.awaiting_data;
                  return (
                    <tr key={i}>
                      <td data-label="Ингредиент"><span className="cv-strong">{dg.ingredient}</span>
                        <span className="cv-rowchips">{(dg.criteria || []).map((c, j) => <Pill key={j} tone="neutral" size="sm" glyph={false}>{c}</Pill>)}</span>
                      </td>
                      <td data-label="Статус"><Pill tone={st.tone} size="sm">{st.label}</Pill></td>
                      <td data-label="Эффект"><TrendCell deltaRub={dg.effect_rub} goodDirection="up" /></td>
                      <td data-label="Рекомендация" className="cv-comment"><span className="text-dim">{dg.recommendation}</span></td>
                      <td data-label="Нужные данные" className="text-mute">{dg.data_needed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CanvasSection>
        {r.action_plan && r.action_plan.length > 0 && (
          <CanvasSection title="План действий">
            <ol className="cv-plan">{r.action_plan.map((a, i) => <li key={i}>{a}</li>)}</ol>
          </CanvasSection>
        )}
        {r.scenarios && (
          <CanvasSection title="Сценарии">
            <div className="cv-recs">
              {['conservative', 'target'].map((k) => { const s = r.scenarios[k]; if (!s) return null; return (
                <div className={'cv-rec' + (k === 'target' ? ' cv-rec--pick' : '')} key={k}>
                  <span className="cv-rec__label">{s.label}</span>
                  <span className="cv-rec__save num">недостача {fmt.money(s.shortage_after_rub)}</span>
                  <span className="cv-rec__meas t-caption text-mute">излишки {fmt.money(s.surplus_after_rub)} · откл. {fmt.percent(s.deviation_pct)}</span>
                </div>
              ); })}
            </div>
          </CanvasSection>
        )}
      </>
    );

    const footer = (
      <div className="cv-foot-actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(openHref)}><span className="btn__label">Открыть отчёт</span><Icon name="arrow-up-right" size={14} /></button>
      </div>
    );
    return <CanvasViewForm {...header} onClose={onClose} openHref={openHref} openLabel="Открыть" footer={footer}>{body}</CanvasViewForm>;
  }

export { InventoryCanvas };
